import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCardComponent } from '@ui/card/card.component';
import { VxCardFooterComponent } from '@ui/card/card-footer.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxInputComponent } from '@ui/input/input.component';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxSelectComponent } from '@ui/select/select.component';
import { VxSelectOption } from '@ui/select/select.types';
import { VxTooltipDirective } from '@ui/tooltip/tooltip.directive';
import { SharedModule } from 'src/app/core/shared.module';
import { HasQuota } from 'src/app/helpers/quota';
import { CreatePageComponent } from 'src/app/layout/create-page/create-page.component';
import { AlertProcessor, AlertSourceType } from 'src/app/models/alert';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import {
  CreateAlertSourceAction,
  ListAlertProcessorsAction,
} from 'src/app/store/alerts/alerts.actions';
import { AlertsState } from 'src/app/store/alerts/alerts.state';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';

const DOCS_URL = 'https://docs.incidite.com/docs/alerting/';
const CONTACT_URL = 'https://incidite.com/contact';

/** Product names, not translated. */
const SOURCE_TYPE_OPTIONS: VxSelectOption<AlertSourceType>[] = [
  { value: AlertSourceType.ALERTMANGER, label: 'Alertmanager' },
];

@Component({
  selector: 'app-page-create-alert-source',
  templateUrl: 'create.component.html',
  imports: [
    SharedModule,
    ReactiveFormsModule,
    RouterLink,
    CheckRoleDirective,
    CreatePageComponent,
    VxButtonComponent,
    VxCardComponent,
    VxCardFooterComponent,
    VxFormFieldComponent,
    VxIconComponent,
    VxInputComponent,
    VxPanelComponent,
    VxSelectComponent,
    VxTooltipDirective,
  ],
  host: { class: 'block' },
})
export class CreateAlertSourcePageComponent implements OnInit {
  private store = inject(Store);
  private router = inject(Router);
  private translation = inject(TranslationService);
  private toast = inject(ToastService);

  protected readonly docsUrl = DOCS_URL;
  protected readonly contactUrl = CONTACT_URL;
  protected readonly typeOptions = SOURCE_TYPE_OPTIONS;

  protected form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    processorId: new FormControl<string | null>(null, Validators.required),
    type: new FormControl(AlertSourceType.ALERTMANGER, {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected isSaving = signal(false);

  private processorEntities = this.store.selectSignal(
    AlertsState.alertProcessors
  );

  private sourcesFeature = this.store.selectSignal(
    SubscriptionState.feature('alerting-sources')
  );

  private processors = computed(() =>
    Object.values(this.processorEntities().entities ?? {})
      .map(entity => entity.data)
      .filter((processor): processor is AlertProcessor => !!processor)
  );

  protected processorOptions = computed<VxSelectOption<string>[]>(() =>
    this.processors().map(processor => ({
      value: processor.id,
      label: processor.name,
      icon: processor.isDefault ? 'star' : undefined,
    }))
  );

  protected hasQuota = computed(() => {
    const feature = this.sourcesFeature();
    return feature ? HasQuota(feature) : false;
  });

  protected quotaTooltip = computed(() => {
    this.translation.currentLocale$();
    return this.hasQuota()
      ? undefined
      : this.translation.translateInstantKey('misc.quotaReached');
  });

  constructor() {
    this.initDefaultProcessor();
  }

  /** Preselects the default processor once the list arrives, unless the user already chose. */
  private initDefaultProcessor(): void {
    effect(() => {
      const processors = this.processors();
      const control = this.form.controls.processorId;
      if (!processors.length || control.value) {
        return;
      }
      const preferred =
        processors.find(processor => processor.isDefault) ?? processors[0];
      control.setValue(preferred.id);
    });
  }

  ngOnInit(): void {
    this.store.dispatch(new ListAlertProcessorsAction());
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, processorId, type } = this.form.getRawValue();
    const trimmedName = name.trim();

    this.isSaving.set(true);
    this.store
      .dispatch(
        new CreateAlertSourceAction({
          processorId: processorId!,
          type,
          name: trimmedName,
          version: 1,
          configuration: {},
        })
      )
      .subscribe({
        next: () => {
          const createdId = this.store.selectSnapshot(
            AlertsState.alertSources
          ).latestCreatedId;
          this.toast.showInfo(
            'pages.settings.alerting.sources.create.toasts.created.header',
            'pages.settings.alerting.sources.create.toasts.created.message',
            { name: trimmedName }
          );
          this.router.navigate(['/settings/alerting/sources', createdId]);
        },
        // the state already surfaces the error as a toast
        error: () => undefined,
      })
      .add(() => this.isSaving.set(false));
  }
}
